// Class with function to export ItemFinder overview data to Excel (Xlsx)

import ExcelJS from 'exceljs';

import {getColumnHeader} from '~/frontend/src/components/base/DataTable/utilities';
import {
    ColumnConfig,
    ColumnDefinition,
    ColumnFilter,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';
import {LoadFinderOverviewDataResult} from '~/shared/src/utilities/modules/finder';
import {AllProps as ItemFinderToolbarAllProps} from '~/frontend/src/components/base/ItemFinder/overview/Toolbar';
import {AllProps as OverviewToolbarAllProps} from '~/frontend/src/components/base/overview/Toolbar';
import {
    AllProps as DataTableAllProps,
    CellValue as DataTableCellValue,
    DataTable,
    defaultProps as dataTableDefaultProps
} from '~/frontend/src/components/base/DataTable';
import {
    getPanelProps,
    getSubPanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {
    processData,
    resolveConditionRecursive
} from '~/frontend/src/utilities/dataUtils';
import cssRule from '~/frontend/src/utilities/cssRule';
import {getValueDisplayName} from '~/frontend/src/utilities/renderHelpers';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import xhr from '~/frontend/src/utilities/xhr';
import {
    LoadDataResult,
    LoadMetaDataRowDataMapping
} from '~/shared/src/utilities/modules/overview';
import {resolveStyleToClassNames} from '~/frontend/src/utilities/valueRenderers';
import {
    isIntlNumberFormatCurrency,
    isIntlNumberFormatUnit
} from '~/shared/src/utilities/valueTesters';
import {
    convertToDate,
    convertValueToString
} from '~/shared/src/utilities/valueConverters';
import {isPropertyValueType} from '~/shared/src/mm/PropertySet';
import {DataStructure} from '~/shared/src/utilities/dataStructure';

type ToolbarAllProps = ItemFinderToolbarAllProps | OverviewToolbarAllProps;

export class XlsxExport {

    // load the (row- and header-)data from the backend for the Xlsx export
    static async loadXlsxExportItemFinderDataFromBackend( toolbarProps : ToolbarAllProps ) : Promise<LoadFinderOverviewDataResult | undefined> {
        const response = await xhr.post(
            `${BASE_URL}/api/finder/loadFinderOverviewData`,
            {
                ...toolbarProps.exportLoadDataParameters,
                start: undefined,
                limit: -1
            },
            undefined,
            `export_xlsx_for_${toolbarProps.uid}`,
            'loadXlsxExportData'
        );

        if ( response?.data ) {
            const responseData = response.data as LoadFinderOverviewDataResult;
            return responseData;
        }
        else {
            // FIXME: Error?
        }

        return undefined;
    }

    static async loadXlsxExportOverviewDataFromBackend( toolbarProps : OverviewToolbarAllProps ) : Promise<LoadDataResult | undefined> {
        const response = await xhr.post(
            `${BASE_URL}/api/overview/loadData`,
            {
                ...toolbarProps.exportLoadDataParameters,
                start: undefined,
                limit: -1
            },
            undefined,
            `export_xlsx_for_${toolbarProps.uid}`,
            'loadXlsxExportData'
        );

        if ( response?.data ) {
            const responseData = response.data as LoadFinderOverviewDataResult;
            return responseData;
        }
        else {
            // FIXME: Error?
        }

        return undefined;
    }

    static async loadXlsxExportHistoryDataFromBackend( loadDataUrl : string, parameters : {
        [ parameter : string ] : any
    } ) : Promise<LoadDataResult | undefined> {
        const response = await xhr.post(
            `${BASE_URL}/api/${loadDataUrl}`,
            {
                ...parameters,
                start: undefined,
                limit: -1
            },
            undefined,
            `export_xlsx_for_history`,
            'loadXlsxExportData'
        );

        if ( response?.data ) {
            const responseData = response.data as LoadFinderOverviewDataResult;
            return responseData;
        }
        else {
            // FIXME: Error?
        }

        return undefined;
    }

    // get the header columns for the Xlsx worksheet
    static getXlsxHeaderColumns( rowDataMapping : LoadMetaDataRowDataMapping, toolbarProps : ToolbarAllProps ) {
        const retVal = [];

        for ( const column of toolbarProps.columns ) {
            const columnDefinition = toolbarProps.columnDefinitions.find( colDef => colDef.field === column.field ) as ColumnDefinition;
            //let width = ( ( columnDefinition.width ?? 20 * widthDivisor ) / widthDivisor ) | 0; // make int from float
            let width = ( typeof column.width === 'number' ? column.width : 100 ) / 6;
            if ( width > 255 ) {
                width = 255;
            }
            retVal.push( {
                header: getColumnHeader(
                    columnDefinition,
                    toolbarProps.dataStructure!,
                    toolbarProps.schema,
                    toolbarProps.rawMode,
                    toolbarProps.showAdditionalRawNames ),
                key   : rowDataMapping[ columnDefinition.field ] ?? columnDefinition.field,
                width : width
                // TODO: add column style
            } );
        }

        return retVal;
    }

    static getColumnDefinitions(
        toolbarProps : ToolbarAllProps,
        dataTable : DataTable,
        data : LoadFinderOverviewDataResult | LoadDataResult
    ) : { columnDefinitions : ColumnDefinition[], numberFormats : ( string | undefined )[] } {
        const columnDefinitions = [];
        const numberFormats = [];
        const dataRow = data.metaData.rowData[ 0 ];

        for ( const column of toolbarProps.columns ) {
            const columnDefinition = toolbarProps.columnDefinitions.find( colDef => colDef.field === column.field ) as ColumnDefinition;
            columnDefinitions.push( columnDefinition );
            if ( toolbarProps.rawMode ) {
                numberFormats.push( undefined );
            }
            else {
                //console.log('dataRow'); console.log(dataRow);
                //console.log('columnDefinition'); console.log(columnDefinition);
                //console.log('column'); console.log(column);
                const cellValue = dataTable.getCellValue( columnDefinition.field, dataRow, undefined, columnDefinition );
                numberFormats.push( XlsxExport.getColumnNumberFormat( cellValue ) );
            }
        }
        return { columnDefinitions, numberFormats };
    }

    // convert a rgba(...) to RGB Hex for column-fill-color
    static rgba2hex( rgba : string ) {
        const
            rgb   = rgba.replace( /\s/g, '' ).match( /^rgba?\((\d+),(\d+),(\d+),?([^,\s)]+)?/i ),
            alpha = ( rgb?.[ 4 ] ?? '' ).trim();

        let a;
        if ( alpha !== '' ) {
            a = parseFloat( alpha );
        }
        else {
            a = 1;
        }

        const hex = rgb ? 'FF' +
            ( ( ( 1 - a ) * 255 + a * parseInt( rgb[ 1 ] ) ) | 1 << 8 ).toString( 16 ).slice( 1 ) +
            ( ( ( 1 - a ) * 255 + a * parseInt( rgb[ 2 ] ) ) | 1 << 8 ).toString( 16 ).slice( 1 ) +
            ( ( ( 1 - a ) * 255 + a * parseInt( rgb[ 3 ] ) ) | 1 << 8 ).toString( 16 ).slice( 1 ) : rgba;

        return hex;
    }

    static getColumnNumberFormat( cellValue : any ) : string | undefined {
        let retVal;

        if ( cellValue ) {
            const valueType = cellValue.valueType ?? cellValue.dataDescriptor?.valueType ?? 'STRING';

            if ( valueType === 'DURATION' ) {
                retVal = '#,##.##0" s"';
            }
            else if ( valueType === 'DATE' ) {
                retVal = '';

                const testUnit : Intl.DateTimeFormatPart[] = Intl.DateTimeFormat( undefined, { day: '2-digit', month: '2-digit', year: 'numeric' } ).formatToParts();

                for ( const part of testUnit ) {
                    if ( part.type === 'day' ) {
                        retVal += 'dd';
                    }
                    else if ( part.type === 'month' ) {
                        retVal += 'mm';
                    }
                    else if ( part.type === 'year' ) {
                        retVal += 'yyyy';
                    }
                    else if ( part.type === 'literal' ) {
                        retVal += part.value;
                    }
                }
            }
            else if ( valueType === 'TIME' ) {
                retVal = '';

                const testUnit : Intl.DateTimeFormatPart[] = Intl.DateTimeFormat( undefined, { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' } ).formatToParts();

                for ( const part of testUnit ) {
                    if ( part.type === 'hour' ) {
                        retVal += 'hh';
                    }
                    else if ( part.type === 'minute' ) {
                        retVal += 'mm';
                    }
                    else if ( part.type === 'second' ) {
                        retVal += 'ss';
                    }
                    else if ( part.type === 'literal' ) {
                        retVal += part.value;
                    }
                }

            }
            else if ( valueType === 'TIMESTAMP' ) {
                retVal = '';

                let testUnit : Intl.DateTimeFormatPart[] = Intl.DateTimeFormat( undefined, { day: '2-digit', month: '2-digit', year: 'numeric' } ).formatToParts();

                for ( const part of testUnit ) {
                    if ( part.type === 'day' ) {
                        retVal += 'dd';
                    }
                    else if ( part.type === 'month' ) {
                        retVal += 'mm';
                    }
                    else if ( part.type === 'year' ) {
                        retVal += 'yyyy';
                    }
                    else if ( part.type === 'literal' ) {
                        retVal += part.value;
                    }
                }

                retVal += ' ';

                testUnit = Intl.DateTimeFormat( undefined, { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' } ).formatToParts();

                for ( const part of testUnit ) {
                    if ( part.type === 'hour' ) {
                        retVal += 'hh';
                    }
                    else if ( part.type === 'minute' ) {
                        retVal += 'mm';
                    }
                    else if ( part.type === 'second' ) {
                        retVal += 'ss';
                    }
                    else if ( part.type === 'literal' ) {
                        retVal += part.value;
                    }
                }
            }
            else if ( cellValue.valueUnit ) {
                if ( valueType === 'CURRENCY' || valueType === 'INTEGER' || valueType === 'DOUBLE' ) {
                    let testUnit : Intl.NumberFormatPart[];

                    if ( valueType === 'CURRENCY' && isIntlNumberFormatCurrency( cellValue.valueUnit ) ) {
                        testUnit = Intl.NumberFormat( undefined, { style: 'currency', currency: cellValue.valueUnit } ).formatToParts( 1111.111111 );
                    }
                    else if ( ( valueType === 'INTEGER' || valueType === 'DOUBLE' ) && isIntlNumberFormatUnit( cellValue.valueUnit ) ) {
                        testUnit = Intl.NumberFormat( undefined, { style: 'unit', unit: cellValue.valueUnit } ).formatToParts( valueType === 'INTEGER' ? 1111 : 1111.111111 );
                    }
                    else {
                        testUnit = Intl.NumberFormat( undefined, {} ).formatToParts( valueType === 'INTEGER' ? 1111 : 1111.111111 );
                        testUnit.push( {
                            type : 'unit',
                            value: cellValue.valueUnit
                        } );
                    }

                    let unit;
                    let numFmt = '#,##';
                    for ( const part of testUnit ) {
                        if ( part.type === 'unit' || part.type === 'currency' ) {
                            unit = part.value;
                        }
                        else if ( part.type === 'fraction' ) {
                            numFmt = numFmt + '.' + part.value.replace( /1/g, '0' );
                        }
                    }

                    if ( testUnit[ 0 ].type === 'unit' ) {
                        retVal = '"' + testUnit[ 0 ].value.replace( /"/g, '\"' ) + ' "' + numFmt;
                    }
                    else {
                        if ( unit !== undefined ) {
                            retVal = numFmt + '" ' + unit.replace( /"/g, '\"' ) + '"';
                        }
                    }
                    // [{"type":"integer","value":"1"},
                    //  {"type":"group","value":"."},
                    //  {"type":"integer","value":"111"},
                    //  {"type":"decimal","value":","},
                    //  {"type":"fraction","value":"111"},
                    //  {"type":"literal","value":" "},
                    //  {"type":"unit","value":"km/h"}]
                }
                else {
                    retVal = '@" ' + cellValue.valueUnit.replace( /"/g, '\"' ) + '"';
                }
            }
        }

        return retVal;
    }

    static getWorksheetTitle( toolbarProps : ToolbarAllProps, panelProps? : PanelProps ) {
        let retVal = 'Data';

        if ( panelProps === undefined ) {
            panelProps = getPanelProps( toolbarProps.uid );
        }

        if ( panelProps?.panelProps?.panelTabTitle ) {
            retVal = panelProps.panelProps.panelTabTitle;
        }
        else if ( ( toolbarProps as OverviewToolbarAllProps ).viewKey ) {
            retVal = ( toolbarProps as OverviewToolbarAllProps ).viewKey;
        }

        // replace all invalid chars for the excel tab title
        retVal = retVal.replace( /[*?:\\\/\[\]]/g, '-' );
        //console.log('Title:', retVal);
        return retVal;
    }

    static async processDataToXslxFile( data : LoadFinderOverviewDataResult | LoadDataResult, toolbarProps : ToolbarAllProps ) {

        // FIXME: das hier besser machen und aus toolbarProps erzeugen?
        let panelProps : any = getPanelProps( toolbarProps.uid );
        if ( !panelProps || !Object.keys( panelProps ).length ) {
            panelProps = getSubPanelProps( toolbarProps.uid );
        }
        const dataTableProps = ( panelProps.panelProps ?? panelProps ?? {} ) as DataTableAllProps;

        //console.log('toolbarProps'); console.log(toolbarProps);
        //console.log('panelProps'); console.log(panelProps);
        //console.log('data'); console.log(data);

        // initialize Excel worksheet
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Yoonite';
        workbook.lastModifiedBy = global.user?.name.toString() ?? 'Yoonite';
        // TODO: add additional meta-data?
        const sheet = workbook.addWorksheet(
            XlsxExport.getWorksheetTitle( toolbarProps, panelProps ),
            { views: [ { state: 'frozen', xSplit: 0, ySplit: 1 } ] }
        );

        sheet.columns = XlsxExport.getXlsxHeaderColumns( data.metaData.rowDataMapping, toolbarProps );

        const myDataContext = 'xlsx_export_' + toolbarProps.uid;
        // FIXME: typing of data.data
        processData( data.data as any, myDataContext, toolbarProps.schema );

        const dataTable = new DataTable( {
            ...dataTableDefaultProps,
            ...toolbarProps, // TODO: das hier kann raus, wenn dataTableProps korrekt funktioniert
            ...dataTableProps,
            dataContext      : myDataContext,
            valueFieldMapping: data.metaData.rowDataMapping
        } );

        const tmp = XlsxExport.getColumnDefinitions( toolbarProps, dataTable, data );
        const columnDefinitions = tmp.columnDefinitions;
        const numberFormats = tmp.numberFormats;

        const valueDefaultColor = XlsxExport.rgba2hex( '' + cssRule( '.status-default', 'color' ) );
        const rawMode : boolean = toolbarProps.rawMode ?? false;

        for ( const dataRow of data.metaData.rowData ) {
            // TODO: add style to first row
            // DataTable/index.tsx getCellValue
            // valueRenderers.tsx renderValue
            const row = [];
            const rowConfig : ( DataTableCellValue | undefined )[] = [];
            let rowFontColor, rowBgColor;
            if ( dataTable.props.rowStyling.length > 0 ) {
                const rowValues = dataTable.getRowValues( dataRow );
                const classNames = resolveStyleToClassNames( dataTable.props.rowStyling, rowValues );
                if ( classNames.length > 0 ) {
                    //console.log('classNames'); console.log(classNames);
                    let cssFontColor, cssBgColor;
                    // last CSS-class wins
                    for ( const className of classNames ) {
                        cssFontColor = cssRule( '.' + className, 'color' ) || cssFontColor;
                        cssBgColor = cssRule( '.' + className, 'background-color' ) || cssBgColor;
                    }
                    if ( cssFontColor ) {
                        rowFontColor = XlsxExport.rgba2hex( '' + cssFontColor );
                    }
                    if ( cssBgColor ) {
                        rowBgColor = XlsxExport.rgba2hex( '' + cssBgColor );
                    }
                }
            }
            //console.log('NewRow');
            //const row = sheet.addRow( [], 'i+' );
            for ( const columnDefinition of columnDefinitions ) {
                // convert values
                const cellValue = dataTable.getCellValue( columnDefinition.field, dataRow, undefined, columnDefinition );
                //console.log('cellValue'); console.log(cellValue);
                //console.log('columnDefinition'); console.log(columnDefinition);
                if ( cellValue !== undefined ) {
                    let value = cellValue.value;

                    if ( Array.isArray( value ) ) {
                        // TODO: do we need to check for valueSet for each enum-value in list?
                        let listConcatenator = ', ';

                        if ( !rawMode && columnDefinition.format?.type === 'Text' && columnDefinition.format?.options?.listConcatenator ) {
                            listConcatenator = columnDefinition.format.options.listConcatenator;
                        }

                        value = value.join( listConcatenator );
                    }
                    else if ( cellValue.valueSetId ) {
                        const valueToDisplay = isPropertyValueType( cellValue.valueType )
                            ? convertValueToString( value as AttributeValue, cellValue.valueType )
                            : String( value );

                        value = getValueDisplayName(
                            cellValue.valueSetId,
                            valueToDisplay,
                            toolbarProps.schema,
                            rawMode,
                            toolbarProps.showAdditionalRawNames );
                    }
                    else if ( ( cellValue.valueType === 'DATE' || cellValue.valueType === 'TIME' || cellValue.valueType === 'TIMESTAMP' ) && typeof value === 'string' ) {
                        value = convertToDate( value, cellValue.valueType );
                    }

                    if ( typeof value === 'string' && value.length > 32767 ) {
                        value = __( 'YN.base.common.xlsx.valueTooLong' ) + ' ' + value;
                        value = value.substring( 0, 32767 );
                        cellValue.statusId = -2;
                    }
                    row.push( value );
                    rowConfig.push( cellValue );
                }
                else {
                    row.push( undefined );
                    rowConfig.push( undefined );
                }
            }
            const sheetRow = sheet.addRow( row );
            if ( rowBgColor ) {
                sheetRow.fill = {
                    type   : 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: rowBgColor } // yes fgColor is correct: it is the fgColor of the fill-pattern
                }
            }

            let column = 0;
            // TODO: merge with for-loop above. use emtpy row-values for initial row-fill or use fill on each cell
            for ( const cellValue of rowConfig ) {
                if ( cellValue ) {
                    const cell = sheetRow.getCell( column + 1 );

                    // FIXME: was gewinnt? row-font-color oder status-font-color (oder cell-conditional-style)?
                    if ( rowFontColor ) {
                        cell.font = { color: { argb: rowFontColor } };
                    }
                    else {
                        // set font-color of cell depending on status
                        if ( cellValue.isDefault ) {
                            cell.font = { color: { argb: valueDefaultColor } };
                        }
                        else if ( cellValue.statusId ) {
                            if ( cellValue.statusId === -2 ) { // mark truncated string value as error with color red
                                cell.font = { color: { argb: 'FFFF0000' } };
                            }
                            else if ( cellValue.valueType !== 'TEXT' ) {
                                const status = metaModel[ toolbarProps.schema ].getStatus( cellValue.statusId );
                                if ( status?.color ) {
                                    cell.font = { color: { argb: status.color } };
                                }
                            }
                        }
                    }
                    /*
                    // optimize this, use numberFormat from column-definition
                    if ( !rawMode ) {
                        const numFmt = XlsxExport.getColumnNumberFormat( cellValue );
                        if ( numFmt !== undefined ) {
                            cell.numFmt = numFmt;
                        }
                    }
                     */
                    if ( !rawMode && numberFormats[ column ] !== undefined ) {
                        cell.numFmt = numberFormats[ column ]!;
                    }

                    // font color conditional styling
                    const columnDefinition = columnDefinitions[ column ];
                    if ( columnDefinition.styling ) {
                        for ( const style of columnDefinition.styling ) {
                            if ( style.condition ) {
                                const values : any = {};
                                values[ columnDefinition.field ] = cellValue.value;
                                const resolvedCondition = resolveConditionRecursive( style.condition, values );
                                if ( resolvedCondition ) {
                                    if ( style.style?.color ) {
                                        let color = style.style.color;
                                        color = color.replace( /^#/, 'FF' );
                                        cell.font = { color: { argb: color } };
                                    }
                                }
                            }
                            else if ( style.style?.color ) {
                                let color = style.style.color;
                                color = color.replace( /^#/, 'FF' );
                                cell.font = { color: { argb: color } };
                            }
                        }
                    }
                }

                column++;
            }
        }

        // clear processed data
        delete global.data[ myDataContext ];

        // make header-row bold
        const headerRow = sheet.getRow( 1 );
        headerRow.eachCell( function( cell, colNumber ) {
            cell.font = { bold: true };
        } );

        const buffer = await workbook.xlsx.writeBuffer();
        return buffer;
    }

    public static async getXlsxFileForOverviewData( toolbarProps : OverviewToolbarAllProps ) {
        const data = await XlsxExport.loadXlsxExportOverviewDataFromBackend( toolbarProps );

        if ( data?.success ) {
            return XlsxExport.processDataToXslxFile( data, toolbarProps );
        }
        else {
            console.log( data );
            return undefined;
        }
    }

    public static async getXlsxFileForItemFinderData( toolbarProps : ItemFinderToolbarAllProps ) {
        const data = await XlsxExport.loadXlsxExportItemFinderDataFromBackend( toolbarProps );

        if ( data?.success ) {
            return XlsxExport.processDataToXslxFile( data, toolbarProps );
        }
        else {
            console.log( data );
            return undefined;
        }
    }

    public static async getXlsxFileForHistoryData( loadDataUrl : string, uid : string, schema : Schema, itemId : number, dataStructure : DataStructure, columnDefinitions : ColumnDefinition[], columns : ColumnConfig[], filters : ColumnFilter[], sorters : ColumnSorter[] ) {
        const data = await XlsxExport.loadXlsxExportHistoryDataFromBackend(
            loadDataUrl,
            {
                schema,
                itemId,
                columns,
                filters,
                sorters
            }
        );

        if ( data?.success ) {
            return XlsxExport.processDataToXslxFile(
                data,
                {
                    uid,
                    schema,
                    dataStructure,
                    columns,
                    columnDefinitions,
                    sorters
                } as ToolbarAllProps
            );
        }
        else {
            console.log( data );
            return undefined;
        }
    }

}
